'use client';
import React, { useState, useEffect } from 'react';
import {
  Container,
  Nav,
  Navbar,
  OverlayTrigger,
  Popover,
} from 'react-bootstrap';
import Button from '@/componentv2/button/button';
import Avatar from '@/componentv2/avatar/avatar';
import HamburgerMenu from './hamburgerMenu/hamburgerMenu';
import NavDropdown from './navDropdown/navDropdown';
import MobileHamburgerMenu from './mobileHamburgerMenu/mobileHamburgerMenu';
import MobileUserMenu from './mobileUserMenu/mobileUserMenu';
import Image from 'next/image';
import Link from 'next/link';
import AppLink from '@/componentv2/AppLink/AppLink';
import './header.scss';
import {
  BadgeIcon,
  NoteIcon,
  SettingIcon,
  CardIcon,
  LogoutIcon,
  MenuCloseIcon,
  MobileBrandLogo,
} from '@/componentv2/icons';
import { useUserProfile, is10DayPass } from '@/contexts/UserProfileContext';
import { LuLoader } from 'react-icons/lu';
import AdminIcon from '../icons/adminIcon';
import { productsNavLinks } from '@/componentv2/nav/productNavLinks';
import { DASHBOARD_NAV_URL, DASHBOARD_URL } from '@/constants/routes';

const moreMenuItems = [
  {
    id: 'templates',
    label: 'Templates',
    href: '/templates',
  },
  {
    id: 'resourcesandtips',
    label: 'Resources & Tips',
    href: '/resources',
  },
  { id: 'blog', label: 'Blog', href: '/blog' },
  {
    id: 'case-studies',
    label: 'Case Studies',
    href: '/case-studies',
  },
  {
    id: 'videos',
    label: 'Videos',
    href: '/videos',
  },
  {
    id: 'enterprise',
    label: 'Enterprise',
    href: '/enterprise',
  },
  {
    id: 'search',
    label: 'Search',
    href: '/findasignup',
  },
  {
    id: 'help-center',
    label: 'Help Center',
    href: 'https://support.signupgenius.com/hc/en-us',
  },
];

const getUserPopoverNavItems = (
  userId?: number,
  isSubAdmin?: boolean,
  isAdmin?: boolean
) => {
  const items = [
    {
      id: 'dashboard',
      icon: NoteIcon,
      label: 'Dashboard',
      href: DASHBOARD_URL,
    },
    {
      id: 'settings',
      icon: SettingIcon,
      label: 'Settings & Preferences',
      href: `/index.cfm?go=m.editprofile&memberid=${userId || ''}`,
    },
    {
      id: 'billing',
      icon: CardIcon,
      label: 'Billing & Payments',
      href: '/index.cfm?go=o.mypurchases',
    },
    {
      id: 'admin',
      icon: AdminIcon,
      label: 'Admin',
      href: '/admin',
    },
    {
      id: 'logout',
      icon: LogoutIcon,
      label: 'Log Out',
      href: '/index.cfm?go=c.logout',
      isDivider: true,
    },
  ];

  // Filter out settings and billing for subadmins
  if (isSubAdmin) {
    return items.filter(
      (item) =>
        item.id !== 'settings' && item.id !== 'billing' && item.id !== 'admin'
    );
  }

  // Filter out admin item if user is not an admin
  if (!isAdmin) {
    return items.filter((item) => item.id !== 'admin');
  }

  return items;
};

const Header: React.FC = () => {
  const { userProfile, userRoles, isLoading, handleLogout } = useUserProfile();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileUserMenuOpen, setIsMobileUserMenuOpen] = useState(false);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    if (!isMobileMenuOpen) {
      setIsMobileUserMenuOpen(false);
    }
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const toggleMobileUserMenu = () => {
    setIsMobileUserMenuOpen(!isMobileUserMenuOpen);
    if (!isMobileUserMenuOpen) {
      setIsMobileMenuOpen(false);
    }
  };

  const closeMobileUserMenu = () => {
    setIsMobileUserMenuOpen(false);
  };

  const handleGoAdFree = () => {
    window.location.href = '/pricing';
  };

  const handleLogoutClick = () => {
    handleLogout();
    window.location.href = '/index.cfm?go=c.logout';
  };

  // Prevent body scroll when mobile menu or user menu is open
  useEffect(() => {
    if (isMobileMenuOpen || isMobileUserMenuOpen) {
      // Prevent scrolling on body
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.height = '100%';
    } else {
      // Restore scrolling
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    }

    return () => {
      // Cleanup on unmount
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
    };
  }, [isMobileMenuOpen, isMobileUserMenuOpen]);

  const userPopover = userProfile ? (
    <Popover id="user-popover" className="user-popover">
      <Popover.Body>
        <div className="user-popover-header">
          {userProfile.profilepicture?.text ? (
            <div className="user-popover-avatar-text">
              {userProfile.profilepicture.text}
            </div>
          ) : (
            <Avatar
              src={
                userProfile.profilepicture?.xlarge ||
                '/n_images/User-logged-in.png'
              }
              userName={`${userProfile.firstname} ${userProfile.lastname}`}
              className="user-popover-avatar"
              rounded
              width={100}
              height={100}
              alt="User Avatar"
            />
          )}
          <div className="user-popover-info">
            <div className="user-name">{`${userProfile.firstname} ${userProfile.lastname}`}</div>
            <div className="user-email">{userProfile.email}</div>
            {userRoles && userRoles.length > 0 && (
              <Link
                href="/MyAccount/ResetAdminID/1?switch=true"
                className="switch-account"
              >
                Switch Account
              </Link>
            )}
          </div>
        </div>

        {!userProfile.issubadmin && (
          <div className="user-popover-section">
            <div className="subscription-badge">
              <div className="badge-text">
                <span className="badge-product-name">
                  {is10DayPass(userProfile)
                    ? 'SignUpGenius - 10-Day Access'
                    : userProfile.subscription?.productname
                        ?.replace(/\s*\(\s*(annual|monthly)\s*\)\s*$/i, '')
                        .replace(/\s+Pro\s+/i, ' ')
                        .replace(/\s+/g, ' ')
                        .trim() || 'No Subscription'}
                </span>
                {!userProfile.ispro && !userProfile.istrial && (
                  <a
                    href="/index.cfm?go=o.ProGeniusProducts&showtrial=1"
                    className="badge-free-trial"
                  >
                    Try Premium Free
                  </a>
                )}
              </div>
              <a href="/index.cfm?go=o.mypurchases" className="manage-link">
                Manage
              </a>
            </div>
            {userProfile.ispro &&
              (userProfile.istrial || is10DayPass(userProfile)) && (
                <div className="subscription-type">Active</div>
              )}
            {userProfile.ispro &&
              !userProfile.istrial &&
              !is10DayPass(userProfile) && (
                <div className="subscription-type">
                  {userProfile.subscription?.productname
                    ?.toLowerCase()
                    .includes('annual')
                    ? 'Annual'
                    : 'Monthly'}
                </div>
              )}
          </div>
        )}
        <nav className="user-popover-nav">
          {getUserPopoverNavItems(
            userProfile.id,
            userProfile.issubadmin,
            userProfile.isadmin
          )
            .filter((item) => item.id !== 'logout')
            .map((item) => {
              const IconComponent = item.icon;
              return (
                <React.Fragment key={item.id}>
                  {item.isDivider && (
                    <div className="user-popover-divider mx-3" />
                  )}
                  <a href={item.href} className="popover-nav-item popover-link">
                    {item.id === 'admin' && (
                      <span className="nav-icon">
                        <IconComponent />
                      </span>
                    )}
                    {item.label}
                  </a>
                </React.Fragment>
              );
            })}
        </nav>
        <div className="popover-logout-wrap">
          <Button
            variant="primary-ghost"
            className="popover-logout-btn"
            onClick={handleLogoutClick}
          >
            Log Out
          </Button>
        </div>
      </Popover.Body>
    </Popover>
  ) : null;

  return (
    <>
      <Navbar expand="lg" className="header">
        <Container>
          <div className="d-lg-none">
            <div className="d-flex align-items-center mobile-header-gap">
              <HamburgerMenu
                onClick={toggleMobileMenu}
                isOpen={isMobileMenuOpen}
              />
              <Link
                href="/"
                className=""
                onClick={() => {
                  closeMobileMenu();
                  closeMobileUserMenu();
                }}
              >
                <Image
                  src="/n_images/SignUpGenius-Logo-FullColor.svg"
                  alt="SignUpGenius Logo"
                  className="header-logo d-none d-md-block"
                  width={182}
                  height={40}
                  priority
                />
                <MobileBrandLogo
                  className="d-block d-md-none"
                  width={35}
                  height={45}
                />
              </Link>
            </div>
          </div>
          <Link href="/" className="d-none d-lg-block">
            <Image
              src="/n_images/SignUpGenius-Logo-FullColor.svg"
              alt="SignUpGenius Logo"
              className="header-logo"
              width={261}
              height={58}
              priority
            />
          </Link>
          {/* <Navbar.Toggle aria-controls="basic-navbar-nav" /> */}
          <Navbar id="basic-navbar-nav">
            <Nav className="me-auto header-center d-none d-lg-flex">
              <NavDropdown
                title="Products"
                items={productsNavLinks}
                variant="products"
                id="products-nav-dropdown"
                isWide
              />
              <AppLink href="/pricing" intent="primary" className="header-link">
                Pricing
              </AppLink>
              <NavDropdown
                title="More"
                items={moreMenuItems}
                variant="more"
                id="more-nav-dropdown"
              />
            </Nav>
            <div className="header-right d-flex align-items-center">
              {userProfile && !userProfile.ispro && !userProfile.istrial && (
                <Button
                  variant="primary-ghost"
                  onClick={handleGoAdFree}
                  className="signup-btn"
                >
                  <span className="d-none d-md-inline">Go Ad Free</span>
                  <span className="d-inline d-md-none">Ad Free</span>
                </Button>
              )}

              <Button
                variant="primary"
                className="signup-btn"
                href={DASHBOARD_NAV_URL}
              >
                {userProfile ? (
                  'Create'
                ) : (
                  <>
                    <span className="d-none d-md-inline">
                      Get Started for Free
                    </span>
                    <span className="d-inline d-md-none">Start Free</span>
                  </>
                )}
              </Button>

              {/* Desktop: Popover or Login Button */}
              {isLoading ? (
                <div className="d-none d-lg-flex loading avatar-wrapper">
                  <LuLoader />
                </div>
              ) : userProfile ? (
                <div className="d-none d-lg-block">
                  <OverlayTrigger
                    trigger="click"
                    placement="bottom-end"
                    overlay={userPopover!}
                    rootClose
                  >
                    <div className="avatar-wrapper">
                      {userProfile.profilepicture?.text ? (
                        <div className="avatar-text-wrapper">
                          {userProfile.profilepicture.text}
                        </div>
                      ) : (
                        <Avatar
                          src={
                            userProfile.profilepicture?.xlarge ||
                            '/n_images/User-logged-in.png'
                          }
                          userName={`${userProfile.firstname} ${userProfile.lastname}`}
                          className="avatar-box"
                          rounded
                          width={45}
                          height={45}
                          alt="User Avatar"
                        />
                      )}
                    </div>
                  </OverlayTrigger>
                </div>
              ) : (
                <Link href="/register" className="d-none d-lg-block login-btn">
                  Log In
                </Link>
              )}
              {/* Mobile Avatar - triggers slide-in menu */}
              {isLoading ? (
                <div className="d-lg-none loading">
                  <LuLoader />
                </div>
              ) : userProfile ? (
                <div className="d-lg-none mobile-avatar-toggle">
                  {isMobileUserMenuOpen ? (
                    <Button
                      className="mobile-close-button"
                      onClick={toggleMobileUserMenu}
                      aria-label="Close user menu"
                      variant="text"
                    >
                      <MenuCloseIcon width={24} height={24} />
                    </Button>
                  ) : (
                    <div
                      className="avatar-wrapper"
                      onClick={toggleMobileUserMenu}
                    >
                      {userProfile.profilepicture?.text ? (
                        <div className="avatar-text-wrapper">
                          {userProfile.profilepicture.text}
                        </div>
                      ) : (
                        <Avatar
                          src={
                            userProfile.profilepicture?.xlarge ||
                            '/n_images/User-logged-in.png'
                          }
                          userName={`${userProfile.firstname} ${userProfile.lastname}`}
                          className="avatar-box"
                          rounded
                          width={45}
                          height={45}
                          alt="User Avatar"
                        />
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  href="/register"
                  className="d-lg-none btn btn-text sgn-btn login-btn"
                >
                  Log In
                </Link>
              )}
            </div>
          </Navbar>
        </Container>
      </Navbar>

      {/* Mobile Menus */}
      <MobileHamburgerMenu
        isOpen={isMobileMenuOpen}
        onClose={closeMobileMenu}
      />
      {userProfile && (
        <MobileUserMenu
          isOpen={isMobileUserMenuOpen}
          onClose={closeMobileUserMenu}
          userProfile={userProfile}
          userRoles={userRoles}
          handleLogout={handleLogoutClick}
        />
      )}
    </>
  );
};

export default Header;
